import React from 'react';
import { useTranslation } from 'react-i18next';
import 'yet-another-react-lightbox/styles.css';
import Lightbox from 'yet-another-react-lightbox';
import {
	Box,
	Button,
	ButtonBase,
	Card,
	CardActions,
	CardContent,
	CardMedia,
	Chip,
	Divider,
	Grid,
	Stack,
	useTheme
} from '@mui/material';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import StairsIcon from '@mui/icons-material/Stairs';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import Typography from '../../ui/typography/Typography';
import { RentingSpacesTableData } from '../../../codelist/_content/RentingSpaces';
import { RentingSpacesTablesDataFragment } from '../../../codelist/content';

const CONTACT_EMAIL = 'info@ochypka.sk';

const labels: Record<string, Record<string, string>> = {
	sk: {
		available: 'Voľné',
		rent: 'Nájomné',
		energy: 'Energie + služby',
		otherServices: 'Ostatné služby',
		perM2: '/m²/mes.',
		total: 'Spolu mesačne',
		exclVat: 'bez DPH',
		contact: 'Mám záujem',
		photo: 'Fotografia',
		note: 'Všetky ceny sú uvedené bez DPH za mesiac. Mesačná suma je orientačná.',
		subject: 'Záujem o prenájom'
	},
	en: {
		available: 'Available',
		rent: 'Rent',
		energy: 'Energy + services',
		otherServices: 'Other services',
		perM2: '/m²/month',
		total: 'Total per month',
		exclVat: 'excl. VAT',
		contact: "I'm interested",
		photo: 'Photo',
		note: 'All prices are per month and exclude VAT. The monthly total is indicative.',
		subject: 'Rental enquiry'
	},
	de: {
		available: 'Frei',
		rent: 'Miete',
		energy: 'Energie + Dienstleistungen',
		otherServices: 'Sonstige Leistungen',
		perM2: '/m²/Monat',
		total: 'Gesamt pro Monat',
		exclVat: 'ohne MwSt.',
		contact: 'Anfrage senden',
		photo: 'Foto',
		note: 'Alle Preise verstehen sich monatlich und ohne MwSt. Der Monatsbetrag ist ein Richtwert.',
		subject: 'Mietanfrage'
	}
};

const localeMap: Record<string, string> = {
	sk: 'sk-SK',
	en: 'en-GB',
	de: 'de-DE'
};

const round2 = (value: number) => Math.round(value * 100) / 100;

const RentingSpaceCard = ({
	space,
	language
}: {
	space: RentingSpacesTablesDataFragment;
	language: string;
}) => {
	const theme = useTheme();
	const [lightboxOpen, setLightboxOpen] = React.useState(false);
	const l = labels[language] || labels.sk;
	const locale = localeMap[language] || localeMap.sk;

	const currency = (value: number) =>
		new Intl.NumberFormat(locale, {
			style: 'currency',
			currency: 'EUR'
		}).format(value);
	const number = (value: number) =>
		new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);

	const rows = [
		{ label: l.rent, perM2: space.rentPerM2 },
		{ label: l.energy, perM2: space.energyPerM2 },
		{ label: l.otherServices, perM2: space.otherServicesPerM2 }
	].map((row) => ({ ...row, monthly: round2(row.perM2 * space.area) }));

	const total = round2(rows.reduce((sum, row) => sum + row.monthly, 0));
	const title = space.title[language] || space.title.sk;
	const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
		`${l.subject} – ${title} (${number(space.area)} m²)`
	)}`;
	const images = space.images || [];

	return (
		<Card
			variant="outlined"
			sx={{
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				borderRadius: 2,
				transition: 'box-shadow .2s, border-color .2s',
				'&:hover': {
					borderColor: theme.palette.secondary.main,
					boxShadow: 3
				}
			}}
		>
			{images.length > 0 && (
				<>
					<ButtonBase
						onClick={() => setLightboxOpen(true)}
						aria-label={`${l.photo} – ${title}`}
						sx={{
							position: 'relative',
							display: 'block',
							width: '100%',
							overflow: 'hidden',
							'& img': { transition: 'transform .3s' },
							'&:hover img': { transform: 'scale(1.04)' }
						}}
					>
						<CardMedia
							component="img"
							image={images[0]}
							alt={`${l.photo} – ${title}`}
							loading="lazy"
							sx={{ aspectRatio: '4 / 3', objectFit: 'cover' }}
						/>
						<Box
							sx={{
								position: 'absolute',
								right: 8,
								bottom: 8,
								display: 'flex',
								alignItems: 'center',
								gap: 0.5,
								px: 1,
								py: 0.5,
								borderRadius: 1,
								color: '#fff',
								backgroundColor: 'rgba(0, 0, 0, 0.55)',
								fontSize: 13
							}}
						>
							<ZoomInIcon fontSize="small" />
							{images.length > 1 && images.length}
						</Box>
					</ButtonBase>
					<Lightbox
						open={lightboxOpen}
						close={() => setLightboxOpen(false)}
						slides={images.map((src) => ({ src, alt: title }))}
						controller={{ closeOnPullDown: true, closeOnBackdropClick: true }}
						render={
							images.length > 1
								? undefined
								: { buttonPrev: () => null, buttonNext: () => null }
						}
					/>
				</>
			)}
			<CardContent sx={{ flexGrow: 1 }}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
					mb={2}
				>
					<Typography variant="h3" fontWeight={700}>
						{title}
					</Typography>
					<Chip label={l.available} color="success" size="small" />
				</Stack>

				<Stack direction="row" alignItems="baseline" spacing={1} mb={2}>
					<SquareFootIcon
						fontSize="small"
						sx={{ alignSelf: 'center', color: 'text.secondary' }}
					/>
					<Typography variant="h4" component="p" fontSize={28} fontWeight={700}>
						{number(space.area)}
					</Typography>
					<Typography variant="h4" component="span" color="text.secondary">
						m²
					</Typography>
				</Stack>

				{space.floor && (
					<Stack direction="row" alignItems="center" spacing={1} mb={2}>
						<StairsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
						<Typography variant="body2">
							{space.floor[language] || space.floor.sk}
						</Typography>
					</Stack>
				)}

				<Divider sx={{ mb: 1.5 }} />

				<Stack spacing={1}>
					{rows.map((row) => (
						<Box key={row.label}>
							<Stack direction="row" justifyContent="space-between">
								<Typography variant="body2">{row.label}</Typography>
								<Typography variant="body2" fontWeight={600}>
									{currency(row.monthly)}
								</Typography>
							</Stack>
							<Typography variant="body2" fontSize={13} color="text.secondary">
								{currency(row.perM2)}
								{l.perM2}
							</Typography>
						</Box>
					))}
				</Stack>

				<Box
					mt={2}
					p={1.5}
					borderRadius={1}
					sx={{ backgroundColor: 'action.hover' }}
				>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="baseline"
					>
						<Typography variant="body2" fontWeight={600}>
							{l.total}
						</Typography>
						<Typography
							variant="h4"
							component="p"
							fontSize={20}
							fontWeight={700}
						>
							{currency(total)}
						</Typography>
					</Stack>
					<Typography
						variant="body2"
						fontSize={12}
						color="text.secondary"
						textAlign="right"
					>
						{l.exclVat}
					</Typography>
				</Box>
			</CardContent>

			<CardActions sx={{ px: 2, pb: 2 }}>
				<Button
					fullWidth
					variant="contained"
					color="secondary"
					startIcon={<MailOutlineIcon />}
					href={mailto}
				>
					{l.contact}
				</Button>
			</CardActions>
		</Card>
	);
};

const RentingSpacesTable = () => {
	const {
		i18n: { language }
	} = useTranslation();
	const l = labels[language] || labels.sk;

	if (!RentingSpacesTableData.length) return null;

	return (
		<Box width="100%" mt={2}>
			<Grid container spacing={3}>
				{RentingSpacesTableData.map((space) => (
					<Grid item xs={12} sm={6} md={4} key={space.id}>
						<RentingSpaceCard space={space} language={language} />
					</Grid>
				))}
			</Grid>
			<Typography
				variant="body2"
				color="text.secondary"
				mt={2}
				textAlign="center"
			>
				{l.note}
			</Typography>
		</Box>
	);
};

export default RentingSpacesTable;
